import { apiDownload, apiGetOrNull, apiRequest, type IDownload } from '@/domains/api';
import type { TeamRoutingAnswers } from '@/domains/auth';
import type {
  IAckItem,
  IAnswerWrite,
  ICollegeSuggestion,
  IConfirmationItem,
  ICreatedResponse,
  ICursor,
  IDefinitionSnapshot,
  IMyCycle,
  IMyAssistantInvitation,
  IMyAssistants,
  IMyIdentifier,
  IMyParticipant,
  IMySubmission,
  IMyTeamRoutingRead,
  IMemberLookupDocument,
  IRespondentLookupDocument,
  IResponseState,
  SelfConfirmation
} from './contracts';

/**
 * survey-api's respondent surface (`/api/v1`), one function per route.
 *
 * Two properties of that contract shape everything above this file:
 *
 * - **Every mutation returns the whole state document.** There are no deltas to
 *   merge; a successful write replaces the client's state outright, which is
 *   also how server-side writes the client never made — computed answers,
 *   count-driven rows, exclusion flips — arrive.
 * - **Every mutation but the cursor carries `baseRevision`.** A mismatch is a
 *   409 rather than a lost write.
 */

const BASE = '/api/v1';

/** survey-api's route constraint on a response's and a repeat row's id (`{id:guid}`): anything
 *  else is refused unsent, since it is spliced into a request path. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A team's routing route, for the id the routing page's address holds. `encodeURIComponent`
 * keeps any value inside one segment — but not a bare `.` or `..`, which it leaves alone and
 * the URL parser behind `fetch` then removes, sending the request to a different route
 * (`participants/../team-routing` is `/my/team-routing`) with the caller's bearer. So those,
 * and `''`, are no team of anybody's, answered `null` without a request — participants-api's
 * `getHub` rule, for the same reason.
 */
function teamRoutingPath(participantId: string): Nullable<string> {
  if (participantId === '' || participantId === '.' || participantId === '..') return null;
  return `${BASE}/my/participants/${encodeURIComponent(participantId)}/team-routing`;
}

export const surveyApi = {
  /**
   * The respondent's dashboard: every survey cycle that is not `planned`, each
   * annotated with the caller's own responses in it. Newest actionable cycle
   * first — `active` before `historical`, then cycle code descending.
   *
   * This is what the surveys page reads, in place of `GET /my/responses`, which
   * answers a narrower question: that one lists live responses only, so a cycle
   * the caller has not started is invisible and so is anything `archived`. A
   * `recycled` response is in neither (survey-api's recycle bin design D6). It
   * also carries neither the `surveyCycleId` a draft is opened against nor the
   * path codes a multi-path survey requires — both of which this
   * app used to take from build-time configuration instead.
   */
  myCycles(signal?: AbortSignal): Promise<IMyCycle[]> {
    return apiRequest<IMyCycle[]>(`${BASE}/my/cycles`, { signal });
  },

  /** A response's submit events, newest first (survey-api's advisor PDF design §4). One with a
   *  null `submissionId` is a response filed without one — a migrated response — whose PDF is a
   *  copy of the answers held now. Empty for a response never submitted. */
  mySubmissions(responseId: string, signal?: AbortSignal): Promise<IMySubmission[]> {
    return apiRequest<IMySubmission[]>(
      `${BASE}/my/responses/${encodeURIComponent(responseId)}/submissions`,
      { signal }
    );
  },

  /** The PDF of a submission — the latest without a `submissionId`. survey-api renders it from
   *  what the advisor saw at submit, never from staff's later corrections. */
  submissionDocument(responseId: string, submissionId?: Nullable<number>): Promise<IDownload> {
    const query = submissionId == null ? '' : `?submission=${submissionId}`;
    return apiDownload(
      `${BASE}/my/responses/${encodeURIComponent(responseId)}/document${query}`
    );
  },

  /**
   * The participants this account acts for — whom the account speaks for, which is a wider set
   * than whom a survey is for. The start affordance no longer reads it: it offers each cycle's
   * `eligibleParticipants` (the teams onboarding plan's Q2), survey-api's own answer per cycle,
   * which leaves out an archived team and carries a team's routing. This is what *names* a
   * participant — a number's holder, a response row on an account that acts for several — and
   * it is read beside the cycles because nothing it names is worth holding the page for.
   */
  myParticipants(signal?: AbortSignal): Promise<IMyParticipant[]> {
    return apiRequest<IMyParticipant[]>(`${BASE}/my/participants`, { signal });
  },

  /**
   * A team's routing as the team reads it, and the gating question it is asked next while its
   * outcome is unknown (survey-api's teams plan R9). null for a participant that is not a team
   * the caller acts for — survey-api answers 404 for both, a team of somebody else's included —
   * and, without asking, for an id that is no path segment (`teamRoutingPath`).
   */
  teamRouting(participantId: string, signal?: AbortSignal): Promise<Nullable<IMyTeamRoutingRead>> {
    const path = teamRoutingPath(participantId);
    if (path === null) return Promise.resolve(null);
    return apiGetOrNull<IMyTeamRoutingRead>(path, { signal });
  },

  /**
   * The team's answers to the gating questions — **every answer so far, each time**: survey-api
   * keeps none between calls. It answers the next question, or, once the answers reach an
   * outcome, the routing with no question, written as the team's own decision. A refusal of an
   * answer is 400 `invalid-body` with `problems[{target: 'answers.<code>'}]` — not registration's
   * `errors` shape — and a routing that is not the team's to answer (Veritant decided it, or its
   * firm routes it alone) is 409 `team-routing-closed`, `source` naming which. null, sending
   * nothing, for an id that is no path segment — the read's "not one of your teams".
   */
  answerTeamRouting(
    participantId: string,
    answers: TeamRoutingAnswers
  ): Promise<Nullable<IMyTeamRoutingRead>> {
    const path = teamRoutingPath(participantId);
    if (path === null) return Promise.resolve(null);
    return apiRequest<IMyTeamRoutingRead>(path, { method: 'POST', body: { answers } });
  },

  /**
   * Every number held about the participants this account acts for, and what anyone has said
   * about each (SR-213 block 3). A row is never left out for being unanswered, and nothing
   * here narrows the list: which rows the catalogue asks about is `selfConfirmation === null`,
   * survey-api's rule, and a filter of this app's would be a second one.
   */
  myIdentifiers(signal?: AbortSignal): Promise<IMyIdentifier[]> {
    return apiRequest<IMyIdentifier[]>(`${BASE}/my/identifiers`, { signal });
  },

  /**
   * The owner's answer about one of those numbers. It only ever sets: a second answer is
   * answered with the first and writes nothing, so the row that comes back — not the button
   * that was pressed — is what the caller renders. 404 for a row that is not this caller's,
   * the same as for one that does not exist.
   */
  confirmIdentifier(
    identifierId: string,
    selfConfirmation: SelfConfirmation
  ): Promise<IMyIdentifier> {
    return apiRequest<IMyIdentifier>(`${BASE}/my/identifier-confirmations`, {
      method: 'POST',
      body: { identifierId, selfConfirmation }
    });
  },

  /**
   * A person's assistants and its pending invitations (SR-487, PFR §8), for the account that
   * holds `self` on it. null — survey-api's 404, or unsent for an id that is no GUID — for any
   * other participant, relation or kind.
   */
  myAssistants(participantId: string, signal?: AbortSignal): Promise<Nullable<IMyAssistants>> {
    if (!GUID.test(participantId)) return Promise.resolve(null);
    return apiGetOrNull<IMyAssistants>(`${BASE}/my/participants/${participantId}/assistants`, {
      signal
    });
  },

  /**
   * Invites an address to act for the person as its assistant: 201 with a new invitation, mailed
   * by survey-api's worker, or 200 with the pending one the address holds already, mailed
   * nothing. It takes no `deliverTo`. 400 `invalid-body` on `email`; 409 `already-linked` for an
   * address linked to the person, `too-many-invitations` while five wait.
   */
  inviteAssistant(participantId: string, email: string): Promise<IMyAssistantInvitation> {
    return apiRequest<IMyAssistantInvitation>(
      `${BASE}/my/participants/${encodeURIComponent(participantId)}/assistants`,
      { method: 'POST', body: { email } }
    );
  },

  /** Revokes a pending assistant invitation: 204, its link redeems nothing; 409
   *  `invitation-not-pending` once it was accepted, revoked or replaced. An accepted assistant
   *  is removed by staff's unlink, not here. */
  async revokeAssistantInvitation(participantId: string, invitationId: string): Promise<void> {
    await apiRequest<void>(
      `${BASE}/my/participants/${encodeURIComponent(participantId)}/assistants/invitations/` +
        encodeURIComponent(invitationId),
      { method: 'DELETE' }
    );
  },

  /**
   * Opens a draft. SR-213 retired `POST /dev/responses` — which existed only in
   * Development and therefore in no production build — and this route replaced it
   * body for body: same members, same 201, same refusals. Since survey-api's
   * teams block no registration opens a response, so this is the one entry point
   * — a registrant's first response included, which is the one survey-api proposes
   * the registration's answers into.
   *
   * `participantId` is always named. The route would infer it for an account linked to
   * exactly one participant and refuse `ambiguous_participant` for several, but every caller
   * starts a participant a cycle's `eligibleParticipants` names, so there is nothing to infer.
   *
   * `pathCode` is sent only where the caller chose a path: a team survey-api routes nothing
   * for. A routed team's start names none — survey-api fills it from a prior response, else
   * from the routing — so an omitted code travels as `null`.
   */
  createResponse(input: {
    surveyCycleId: string;
    participantId: string;
    pathCode?: string;
  }): Promise<ICreatedResponse> {
    return apiRequest<ICreatedResponse>(`${BASE}/responses`, {
      method: 'POST',
      body: {
        surveyCycleId: input.surveyCycleId,
        participantId: input.participantId,
        pathCode: input.pathCode ?? null
      }
    });
  },

  /** null when the response does not exist **or** is not this caller's — the
   *  API answers 404 for both on purpose, so a 403 cannot confirm existence. */
  getResponse(responseId: string, signal?: AbortSignal): Promise<Nullable<IResponseState>> {
    return apiGetOrNull<IResponseState>(`${BASE}/responses/${responseId}`, { signal });
  },

  getDefinition(
    responseId: string,
    signal?: AbortSignal
  ): Promise<Nullable<IDefinitionSnapshot>> {
    return apiGetOrNull<IDefinitionSnapshot>(`${BASE}/responses/${responseId}/definition`, {
      signal
    });
  },

  /** All-or-nothing: one rejected write refuses the whole batch (422). */
  writeAnswers(
    responseId: string,
    baseRevision: number,
    writes: IAnswerWrite[]
  ): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/answers`, {
      method: 'POST',
      body: { baseRevision, writes }
    });
  },

  addRepeatRow(responseId: string, baseRevision: number, usage: string): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/repeats`, {
      method: 'POST',
      body: { baseRevision, action: 'add', usage }
    });
  },

  removeRepeatRow(
    responseId: string,
    baseRevision: number,
    instanceId: string
  ): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/repeats`, {
      method: 'POST',
      body: { baseRevision, action: 'remove', instanceId }
    });
  },

  acknowledge(
    responseId: string,
    baseRevision: number,
    acks: IAckItem[]
  ): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/acks`, {
      method: 'POST',
      body: { baseRevision, acks }
    });
  },

  /**
   * Looks a repeat row's member up by the CRD the row holds (SR-416, ML §6): the registries are
   * asked at once, in the API process, and the names are proposed into the row — a draft only.
   * `null`, unsent, for an id that is no GUID. Called by the runtime after the flush that wrote
   * the CRD, never by a press (`useResponse`).
   */
  lookupMember(
    responseId: string,
    instanceId: string,
    baseRevision: number
  ): Promise<Nullable<IMemberLookupDocument>> {
    if (!GUID.test(responseId) || !GUID.test(instanceId)) return Promise.resolve(null);
    return apiRequest<IMemberLookupDocument>(
      `${BASE}/responses/${responseId}/members/${instanceId}/lookup`,
      { method: 'POST', body: { baseRevision } }
    );
  },

  /**
   * "Not this person" (SR-469, B1-11): the row's lookup undone — its looked-up names dropped,
   * proposed or confirmed, and its pins — the CRD kept. Answers the state document; 422
   * `lead_row` on a locked lead row. `null`, unsent, for an id that is no GUID.
   */
  unlinkMember(
    responseId: string,
    instanceId: string,
    baseRevision: number
  ): Promise<Nullable<IResponseState>> {
    if (!GUID.test(responseId) || !GUID.test(instanceId)) return Promise.resolve(null);
    return apiRequest<IResponseState>(
      `${BASE}/responses/${responseId}/members/${instanceId}/unlink`,
      { method: 'POST', body: { baseRevision } }
    );
  },

  /**
   * US colleges matching what a school box holds (SR-469, B3-4), best first — `suggest:
   * 'colleges'`. A suggestion is display only: the answer stays the text typed. survey-api
   * refuses a blank `q` (400 `invalid-query`), so a blank one is answered `[]` unsent; `limit`
   * is 1–25, survey-api's default 10.
   */
  async collegeSuggestions(
    q: string,
    limit?: number,
    signal?: AbortSignal
  ): Promise<ICollegeSuggestion[]> {
    if (q.trim() === '') return [];
    const query = new URLSearchParams({ q });
    if (limit !== undefined) query.set('limit', String(limit));
    const found = await apiRequest<{ items: ICollegeSuggestion[] }>(
      `${BASE}/suggestions/colleges?${query}`,
      { signal }
    );
    return found.items;
  },

  /** The respondent's own CRD looked up (FSP), on `lookupMember`'s terms. */
  lookupRespondent(
    responseId: string,
    baseRevision: number
  ): Promise<Nullable<IRespondentLookupDocument>> {
    if (!GUID.test(responseId)) return Promise.resolve(null);
    return apiRequest<IRespondentLookupDocument>(`${BASE}/responses/${responseId}/lookup`, {
      method: 'POST',
      body: { baseRevision }
    });
  },

  confirmPrefill(
    responseId: string,
    baseRevision: number,
    items: IConfirmationItem[]
  ): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/prefill/confirmations`, {
      method: 'POST',
      body: { baseRevision, items }
    });
  },

  /**
   * Where the respondent is. Deliberately outside the revision protocol —
   * last-write-wins between the respondent's own tabs is the wanted semantics —
   * so it neither carries `baseRevision` nor moves the revision.
   */
  async setCursor(responseId: string, cursor: ICursor): Promise<void> {
    await apiRequest<void>(`${BASE}/responses/${responseId}/cursor`, {
      method: 'PUT',
      body: { cursor }
    });
  },

  /** 422 `submit-blocked` carries the findings report; anything else is a real error. */
  submit(responseId: string, baseRevision: number): Promise<IResponseState> {
    return apiRequest<IResponseState>(`${BASE}/responses/${responseId}/submit`, {
      method: 'POST',
      body: { baseRevision }
    });
  }
};
